import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, imgSrcSet, money, localWebp } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Heptagon, HeptagonPattern } from "@/components/Logo";
import { NewsletterSection } from "@/components/NewsletterSection";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";

const HERO_IMG = "/products/legacy-set-yellow.jpg";
const HERO_WEBP = "/products/legacy-set-yellow-800.webp";
const HERO_SRCSET =
  "/products/legacy-set-yellow-480.webp 480w, /products/legacy-set-yellow-800.webp 800w, /products/legacy-set-yellow-1200.webp 1200w";
const STORY_IMG = "https://images.unsplash.com/photo-1768084356884-22bb77e76931";

const Hero = () => (
  <section className="relative min-h-screen overflow-hidden bg-black" data-testid="hero-section">
    <HeptagonPattern opacity={0.05} />
    <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#6D28D9]/25 blur-[140px]" />
    <div className="absolute top-1/4 right-1/3 w-[400px] h-[400px] rounded-full bg-[#0FB5A3]/12 blur-[140px]" />

    <div className="relative max-w-[1600px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-2 min-h-screen items-center gap-10 pt-28 lg:pt-20 pb-16">
      {/* Copy */}
      <div className="order-2 lg:order-1">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
          className="mb-8 flex items-center gap-5"
        >
          <img
            src="/brand-logo.jpg"
            alt="7FIFTY7 official logo"
            data-testid="hero-brand-logo"
            className="h-20 sm:h-24 w-auto border border-[#C9A24B]/40 shadow-[0_0_40px_rgba(109,40,217,0.4)]"
          />
          <Heptagon size={52} animate className="glow-turquoise hidden sm:block" />
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="label-757 mb-5"
        >
          7FIFTY7™ · WE NOW US NEXT
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          className="font-display font-semibold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04] max-w-xl"
          data-testid="hero-headline"
        >
          Legacy Is
          <br />
          <span className="text-gradient-757">Built Daily.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="text-white/60 text-base mt-6 max-w-md leading-relaxed"
        >
          Luxury lifestyle apparel inspired by ambition, progress, and purpose.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 mt-10"
        >
          <Link to="/shop" className="btn-757" data-testid="hero-shop-cta">
            Shop Collection <ArrowRight size={14} />
          </Link>
          <a href="#movement" className="btn-757-outline" data-testid="hero-movement-cta">
            Join The Movement
          </a>
        </motion.div>
      </div>

      {/* Image */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3, duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
        className="order-1 lg:order-2 relative"
      >
        <div className="relative max-w-md mx-auto lg:ml-auto lg:mr-0">
          <img
            src={HERO_WEBP}
            srcSet={HERO_SRCSET}
            sizes="(max-width: 640px) 100vw, 40vw"
            alt="7FIFTY7 Legacy Set Yellow"
            width="600"
            height="750"
            fetchPriority="high"
            decoding="async"
            data-testid="hero-product-image"
            className="w-full h-[58vh] lg:h-[76vh] object-cover object-top border border-white/10 shadow-[0_0_80px_rgba(109,40,217,0.3)]"
          />
          <div className="absolute -bottom-5 -left-5">
            <Heptagon size={84} className="glow-purple" />
          </div>
          <Link
            to="/product/legacy-set-yellow"
            data-testid="hero-image-product-link"
            className="absolute bottom-5 right-5 bg-black/70 backdrop-blur border border-[#C9A24B]/40 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase text-white hover:text-[#C9A24B] transition-colors"
          >
            Legacy Set Yellow →
          </Link>
        </div>
      </motion.div>
    </div>

    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2">
      <span className="text-[9px] tracking-[0.4em] uppercase text-white/40">Scroll</span>
      <div className="w-px h-10 bg-gradient-to-b from-[#0FB5A3] to-transparent" />
    </div>
  </section>
);

const Marquee = () => (
  <div className="border-y border-white/10 bg-black overflow-hidden py-4">
    <div className="flex whitespace-nowrap animate-marquee">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="flex shrink-0">
          {["Legacy Is Built Daily", "We Now Us Next", "Numbered Editions", "Free Shipping Over $150"].map((t) => (
            <span key={t} className="mx-8 text-[11px] tracking-[0.35em] uppercase text-white/40 flex items-center gap-8">
              {t} <span className="text-[#C9A24B]">⬡</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const FeaturedCard = ({ product, index }) => {
  const { addToCart } = useStore();
  const [size, setSize] = useState(null);

  const handleAdd = () => {
    if (!size) return toast.error("Please select a size");
    const color = product.colors?.[0]?.name || null;
    addToCart(product, size, 1, color);
  };

  const heroImg = product.images[0];
  const isLocal = /^\/products\//.test(heroImg);
  const src = isLocal ? localWebp(heroImg, 800) : imgUrl(heroImg, 900);
  const { srcSet, sizes } = imgSrcSet(heroImg);

  return (
    <Reveal delay={index * 0.12}>
      <div className="border border-white/10 bg-[#0A0A0A] hover:border-[#0FB5A3]/30 transition-colors group" data-testid={`featured-product-${product.slug}`}>
        <Link to={`/product/${product.slug}`} className="block img-zoom">
          <img
            src={src}
            srcSet={srcSet}
            sizes={sizes}
            alt={product.name}
            width="800"
            height="1000"
            loading="lazy"
            decoding="async"
            className="w-full h-[420px] sm:h-[480px] object-cover object-top"
          />
        </Link>
        <div className="p-7">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <Link to={`/product/${product.slug}`}>
                <h3 className="font-display text-lg text-white tracking-wide group-hover:text-[#0FB5A3] transition-colors">
                  {product.name}
                </h3>
              </Link>
              <p className="text-[10px] text-white/35 uppercase tracking-[0.25em] mt-1.5">
                {product.collection} Collection
              </p>
            </div>
            <p className="text-lg text-white font-display shrink-0" data-testid={`featured-price-${product.slug}`}>
              {money(product.price)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 mb-5" data-testid={`featured-sizes-${product.slug}`}>
            {product.sizes.map((s) => (
              <button
                key={s}
                data-testid={`featured-size-${product.slug}-${s}`}
                onClick={() => setSize(s)}
                className={`min-w-[44px] px-2.5 py-2 text-[11px] tracking-wider border transition-colors ${
                  size === s
                    ? "border-[#0FB5A3] bg-[#0FB5A3]/10 text-[#0FB5A3]"
                    : "border-white/20 text-white/60 hover:border-white/60 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button onClick={handleAdd} className="btn-757 w-full" data-testid={`featured-add-to-cart-${product.slug}`}>
            <ShoppingBag size={14} /> Add To Cart
          </button>
        </div>
      </div>
    </Reveal>
  );
};

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/products/legacy-tee-white"),
      api.get("/products/legacy-set-red"),
    ]).then((rs) => setFeatured(rs.map((r) => r.data))).catch(() => {});
  }, []);

  return (
    <div className="bg-black">
      <SEO
        description="7FIFTY7™ — Legacy Is Built Daily. Luxury lifestyle apparel inspired by ambition, progress, and purpose. WE NOW US NEXT"
        schema={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "7FIFTY7",
          description: "Luxury lifestyle apparel inspired by ambition, progress, and purpose.",
          slogan: "WE NOW US NEXT",
        }}
      />
      <Hero />
      <Marquee />

      {/* Featured Collection */}
      <section className="max-w-[1300px] mx-auto px-5 lg:px-10 py-24" data-testid="featured-section">
        <Reveal>
          <div className="text-center mb-14">
            <p className="label-757 mb-3">The Legacy Line</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">Featured Collection</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {featured.map((p, i) => (
            <FeaturedCard key={p.id} product={p} index={i} />
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="text-center mt-12">
            <Link to="/shop" className="btn-757-outline" data-testid="featured-view-all">
              View Full Collection <ArrowRight size={13} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Brand Story — More Than Clothing */}
      <section className="relative border-y border-white/10 overflow-hidden" data-testid="story-section">
        <HeptagonPattern opacity={0.04} />
        <div className="max-w-[1600px] mx-auto px-5 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative img-zoom">
              <img src={imgUrl(STORY_IMG, 900)} alt="7FIFTY7 brand story" loading="lazy" decoding="async" width="900" height="520" className="w-full h-[520px] object-cover" />
              <div className="absolute -bottom-5 -right-5 hidden lg:block">
                <Heptagon size={90} className="glow-purple" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="label-757 mb-4">The Origin</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight leading-tight mb-6" data-testid="story-heading">
              More Than Clothing
            </h2>
            <p className="text-white/50 leading-relaxed mb-4">
              7FIFTY7 represents progress, discipline, and the pursuit of legacy. Every piece is designed for those building something greater than themselves.
            </p>
            <p className="text-white/50 leading-relaxed mb-8">
              Rooted in Haitian heritage and the spirit of 1804 — seven sides, seven generations, numbered editions designed to be inherited, not replaced.
            </p>
            <Link to="/about" className="btn-757-outline" data-testid="story-about-cta">
              Read The Full Story <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Newsletter */}
      <div id="movement">
        <NewsletterSection label="The Inner Circle" title="Become Part Of The Legacy" />
      </div>
    </div>
  );
}
